import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./businessSolutions.css";
import s5 from "../../../assets/home/s5.webp";
import s6 from "../../../assets/home/s6.webp";
import s7 from "../../../assets/home/s7.webp";
import s8 from "../../../assets/home/s8.webp";

gsap.registerPlugin(ScrollTrigger);

const businessServices = [
  {
    id: "01",
    title: "Build, Operate, Transfer / Lean GCC",
    description:
      "Launch and scale dedicated operations with structured setup, ongoing management and a planned transition.",
    image: s7,
    imageAlt: "Build, operate and transfer services",
    href: "/global-capability/bot-lean-gcc",
  },
  {
    id: "02",
    title: "Knowledge Process Outsourcing",
    description:
      "Streamline knowledge-intensive operations with specialised expertise, research and process support.",
    image: s6,
    imageAlt: "Knowledge process outsourcing",
    href: "/global-capability/kpo",
  },
  {
    id: "03",
    title: "Business Process Outsourcing",
    description:
      "Improve operational efficiency with flexible business process support tailored to your requirements.",
    image: s8,
    imageAlt: "Business process outsourcing",
    href: "/global-capability/bpo",
  },
  {
    id: "04",
    title: "Dedicated Staffing",
    description:
      "Build skilled, dedicated teams aligned with your business needs, workflows and delivery goals.",
    image: s5,
    imageAlt: "Dedicated staffing services",
    href: "/global-capability/dedicated-staffing",
  },
];

function BusinessCard({ service }) {
  return (
    <Link
      to={service.href}
      className="biz-card"
      aria-label={`Explore ${service.title}`}
    >
      <div className="biz-card__image-wrap">
        <img
          src={service.image}
          alt={service.imageAlt}
          className="biz-card__image"
          loading="lazy"
          decoding="async"
          width="900"
          height="650"
        />
        <span className="biz-card__image-shade" />
        <div className="biz-card__overlay">
          <div className="biz-card__copy">
            <h3>{service.title}</h3>
            <p>{service.description}</p>
          </div>
          <span className="biz-card__link">
            View service
            <ArrowUpRight size={16} strokeWidth={1.8} />
          </span>
        </div>
        <span className="biz-card__image-arrow">
          <ArrowUpRight size={20} strokeWidth={1.7} />
        </span>
      </div>
    </Link>
  );
}

export default function BusinessSolutions() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const mm = gsap.matchMedia();

    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.from(".biz-intro__eyebrow", {
        autoAlpha: 0,
        duration: 0.6,
        ease: "power1.out",
        scrollTrigger: {
          trigger: ".biz-intro",
          start: "top 85%",
          once: true,
        },
      });

      gsap.from(".biz-intro__title .biz-line", {
        autoAlpha: 0,
        duration: 0.7,
        stagger: 0.18,
        ease: "power1.out",
        scrollTrigger: {
          trigger: ".biz-intro__title",
          start: "top 85%",
          once: true,
        },
      });

      gsap.from(".biz-intro__description", {
        autoAlpha: 0,
        duration: 0.7,
        delay: 0.15,
        ease: "power1.out",
        scrollTrigger: {
          trigger: ".biz-intro__description",
          start: "top 88%",
          once: true,
        },
      });

      gsap.from(".biz-grid .biz-card", {
        autoAlpha: 0,
        duration: 0.6,
        stagger: 0.25,
        ease: "power1.out",
        clearProps: "opacity,visibility",
        scrollTrigger: {
          trigger: ".biz-grid",
          start: "top 86%",
          once: true,
        },
      });
    }, section);

    document.fonts?.ready.then(() => ScrollTrigger.refresh());

    return () => mm.revert();
  }, []);

  return (
    <section className="biz-solutions" ref={sectionRef}>
      <div className="biz-solutions__inner">
        <header className="biz-intro">
          <div className="biz-intro__main">
            <span className="biz-intro__eyebrow">
              <span className="biz-intro__dot" />
              BUSINESS SOLUTIONS
            </span>

            <h2 className="biz-intro__title">
              <span className="biz-line-wrap">
                <span className="biz-line">Global staffing and</span>
              </span>
              <span className="biz-line-wrap">
                <span className="biz-line">business process services,</span>
              </span>
              <span className="biz-line-wrap">
                <span className="biz-line">
                  built to <em>scale.</em>
                </span>
              </span>
            </h2>
          </div>

          <p className="biz-intro__description">
            From dedicated teams to fully managed operations, Qualitas Global
            helps you build, run and grow business functions with the right
            people and processes, so your internal team can stay focused on
            core priorities.
          </p>
        </header>

        <div className="biz-grid">
          {businessServices.map((service) => (
            <BusinessCard key={service.id} service={service} />
          ))}
        </div>
      </div>
    </section>
  );
}
